'use client';

import React, { useState, useEffect, useMemo, useRef, useTransition } from 'react';
import { Search, MapPin, User, Home, Sparkles, X, BedDouble, Bath, DollarSign, Building2, HelpCircle, Clock, SlidersHorizontal } from 'lucide-react';
import LazyFilterPanel, { preloadFilterPanel } from '@/components/filters/LazyFilterPanel';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import Transition from '@/components/ui/Transition';
import { getSearchSuggestions } from '@/app/actions/property';
import { propertyTypeLabel } from '@/lib/property-types';
import { agencyPath } from '@/lib/agency-slug';
import { formatMoney } from '@/lib/format';
import { MAX_PHRASE, featureParams, hasNonPlaceParts, parseSearchQuery, type ParsedQuery } from '@/lib/search-query';
import { featureChips } from '@/lib/search-features';
import { resultsPathFor } from '@/lib/results-paging';
import { provinceName } from '@/lib/provinces';
import type { Suggestions } from '@/lib/search-suggest';
import { loadPlaceIndex, mergePlaceSuggestions, placeKey, resolvePlace, resolvePlaceIn, searchPlaces, type PlaceIndex, type PlaceSuggestion } from '@/lib/place-search';
import { listingPathFor } from '@/lib/place-url';

// How long the search field's example takes to fade out, and then in again.
const HINT_FADE_MS = 500;

interface HomepageSearchHeaderProps {
  lang: string;
  agencyId?: string;
  /** Where a search goes; the page it is on when not given (an agency site). */
  resultsPath?: string;
  /** A "Filters" button in the bar (the home page, where no results bar has one). */
  filtersButton?: boolean;
  /** The kind of listings this page shows, on a results page (rentals, 2026-09-24). */
  deal?: 'sale' | 'rent';
  /** The Buy / Rent switch over the bar: the home page and the two results pages. */
  dealSwitch?: boolean;
}

// What a search keeps when it moves between sale and rent: the place and the
// home, not the price - a sale's $500,000 means nothing as a monthly rent.
const KEPT_ACROSS_DEALS = ['q', 'city', 'province', 'bedrooms', 'bathrooms', 'propertyType', 'petFriendly', 'amenities'];

type NLPParsed = ParsedQuery;

const HighlightMatch = ({ text, highlight }: { text: string; highlight: string }) => {
  if (!highlight || !text) return <span>{text}</span>;
  const index = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (index === -1) return <span>{text}</span>;
  
  const before = text.substring(0, index);
  const match = text.substring(index, index + highlight.length);
  const after = text.substring(index + highlight.length);
  
  return (
    <span>
      {before}
      <span className="text-champagne font-bold">{match}</span>
      {after}
    </span>
  );
};

// "rent" or "for sale" on its own names a deal, not a place: the suggestions must not match it against place names.
function isDealWordOnly(text: string): boolean {
  const p = parseSearchQuery(text.trim());
  return Boolean(p.deal) && !p.city && !hasNonPlaceParts(p);
}

export default function HomepageSearchHeader({ lang, agencyId, resultsPath, filtersButton = false, deal, dealSwitch = false }: HomepageSearchHeaderProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  // What is typed so far, as filters, taken when the panel opens: computed
  // during render it read the place index ref, which does not re-render.
  const [filtersStart, setFiltersStart] = useState('');
  // Buy or rent: the page's own kind, or sale on the home page.
  const [mode, setMode] = useState<'sale' | 'rent'>(deal ?? 'sale');
  const pageDeal = deal ?? 'sale';
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFr = lang === 'fr';

  const translations = {
    placeholder: mode === 'rent'
      ? isFr
        ? "Ville, chambres, loyer... (ex: Montréal 4½ max 1 500 $)"
        : "Search city, beds, rent... (e.g. Toronto 2 beds under 2000)"
      : isFr
        ? "Ville, chambres, prix... (ex: Montréal 2 chambres max 1M)"
        : "Search city, beds, price... (e.g. Toronto 2 beds under 1M)",
    placeholderStandard: isFr
      ? "Rechercher une ville, une agence ou une annonce..."
      : "Search by city, agency or listing...",
    smartMatch: isFr ? "Paramètres détectés :" : "Recognized parameters:",
    applyFilters: isFr ? "Appliquer ces filtres" : "Apply these filters",
    beds: isFr ? "chambres" : "beds",
    baths: isFr ? "salles de bain" : "baths",
    under: isFr ? "max" : "under",
    cities: isFr ? "Lieux" : "Places",
    province: isFr ? "province" : "province",
    listingsCount: (n: number) => (isFr ? `${n} annonce${n > 1 ? 's' : ''}` : `${n} listing${n > 1 ? 's' : ''}`),
    noListingsYet: isFr ? "Aucune annonce pour l’instant" : "No listings yet",
    removeChip: isFr ? "Retirer" : "Remove",
    realtors: isFr ? "Agences et courtiers" : "Agencies & brokers",
    properties: isFr ? "Annonces" : "Listings",
    howToSearch: isFr ? "Comment rechercher en langage naturel ?" : "How to search in natural language?",
    howToSearchDesc: isFr
      ? "Saisissez des phrases complètes. Le moteur extrait automatiquement la ville, le prix, les chambres et le type."
      : "Type full phrases naturally. The engine automatically extracts city, price, bedrooms, and property type.",
    howToSearchStandard: isFr ? "Recherche standard" : "Standard Search",
    howToSearchDescStandard: isFr
      ? "Recherche de correspondances exactes par ville, nom d'agence ou titre d'annonce."
      : "Search for exact matches by city, agency name or listing title.",
    trySearching: isFr ? "Exemples recommandés :" : "Recommended examples:",
    trySearchingStandard: isFr ? "Villes populaires :" : "Popular cities:",
    noDbMatches: isFr
      ? "Aucun résultat exact, mais vous pouvez appliquer les filtres détectés :"
      : "No exact matches found, but you can apply the detected filters:",
    noMatchAtAll: isFr
      ? "Aucun lieu ni aucune annonce ne correspond à cette recherche. Vérifiez l’orthographe ou essayez le nom d’une ville."
      : 'No place or listing matches this search. Check the spelling, or try the name of a city.',
    smartSearchActive: isFr ? "Recherche intelligente : Activée" : "Smart Search: Enabled",
    smartSearchDisabled: isFr ? "Recherche intelligente : Désactivée" : "Smart Search: Disabled",
    recentSearches: isFr ? "Recherches récentes" : "Recent Searches",
    clear: isFr ? "Effacer" : "Clear",
    fromPrice: isFr ? "à partir de" : "from"
  };

  const [searchQuery, setSearchQuery] = useState((searchParams?.get('q') || '').slice(0, MAX_PHRASE));
  // Natural-language parsing is always on; plain database suggestions are shown alongside it.
  const isSmartSearchEnabled = true;

  // On a phone the full placeholder ("Search city, beds, price... (e.g. ...)")
  // was cut to "Search city, beds,..." - the useful part, the example, never
  // showed. Short examples take turns instead, each of which fits (the owner's
  // choice, 2026-09-22). They stop while the person types or has the field
  // focused, and never move for anyone who asked the system for less motion.
  // Wider screens keep the full placeholder.
  const searchHints = isSmartSearchEnabled
    ? mode === 'rent'
      ? isFr
        ? ['Montréal, 4½', 'Condo max 1 500 $', 'Appartement à Laval', 'Québec, 3 chambres']
        : ['Montreal, 2 beds', 'Condo under 2000', 'Apartment in Laval', 'Quebec City, 3 beds']
      : isFr
      ? ['Montréal, 2 chambres', 'Condo max 500 k$', 'Maison à Laval', 'Québec, 3 chambres']
      : ['Montreal, 2 beds', 'Condo under 500K', 'House in Laval', 'Quebec City, 3 beds']
    : isFr
      ? ['Une ville', 'Une agence', 'Une annonce']
      : ['A city', 'An agency', 'A listing'];
  const [hintIndex, setHintIndex] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  // A crossfade in place: the example fades out, changes while invisible and
  // fades back in. It first swapped instantly and slid the new one up from
  // below, which the owner saw as a jerk.
  useEffect(() => {
    if (searchQuery || searchFocused) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let swap: ReturnType<typeof setTimeout> | undefined;
    // Visible again whenever rotation (re)starts, e.g. after the field is cleared.
    const show = setTimeout(() => setHintVisible(true), 0);
    const id = setInterval(() => {
      setHintVisible(false);
      swap = setTimeout(() => {
        setHintIndex((i) => (i + 1) % searchHints.length);
        setHintVisible(true);
      }, HINT_FADE_MS);
    }, 3200);
    return () => {
      clearTimeout(show);
      clearTimeout(swap);
      clearInterval(id);
    };
  }, [searchQuery, searchFocused, searchHints.length]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [parsedNLP, setParsedNLP] = useState<NLPParsed>({});
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // Hydrate persistence from localStorage on mount
  useEffect(() => {
    const savedRecent = localStorage.getItem('recent_searches');
    if (savedRecent) {
      try {
        setRecentSearches(JSON.parse(savedRecent));
      } catch (e) {
        console.error('Error parsing recent searches', e);
      }
    }
  }, []);

  const saveSearchToHistory = (query: string) => {
    if (!query || !query.trim()) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setRecentSearches((prev) => {
      const filtered = prev.filter((q) => q.toLowerCase() !== trimmed.toLowerCase());
      const updated = [trimmed, ...filtered].slice(0, 5);
      localStorage.setItem('recent_searches', JSON.stringify(updated));
      return updated;
    });
  };
  const [suggestions, setSuggestions] = useState<Suggestions>({ cities: [], counts: [], realtors: [], properties: [] });
  // Canada's places, loaded the first time the box is used (lib/place-search.ts),
  // and which of them the last answer counted listings for.
  const [placeIndex, setPlaceIndex] = useState<PlaceIndex | null>(null);
  const placeIndexRef = useRef<PlaceIndex | null>(null);
  const [countedKeys, setCountedKeys] = useState<ReadonlySet<string>>(new Set());
  const ensurePlaces = () => {
    if (placeIndexRef.current) return;
    loadPlaceIndex()
      .then((ix) => {
        placeIndexRef.current = ix;
        setPlaceIndex(ix);
      })
      .catch(() => undefined);
  };
  // Parts of the phrase the visitor took off the recognised list (the x on a
  // chip); they stay off until the phrase is edited.
  const [dropped, setDropped] = useState<(keyof NLPParsed)[]>([]);
  const withoutDropped = (nlp: NLPParsed): NLPParsed => {
    const out = { ...nlp };
    for (const key of dropped) delete out[key];
    return out;
  };
  const effectiveNLP = withoutDropped(parsedNLP);
  // What the phrase was understood to say, one chip per part; the x on a chip
  // takes that part off before the filters are applied.
  const nlpChips = (n: NLPParsed): { key: string; drop: (keyof NLPParsed)[]; icon: React.ReactNode; label: string }[] => {
    const chips: { key: string; drop: (keyof NLPParsed)[]; icon: React.ReactNode; label: string }[] = [];
    if (n.city || n.province) {
      chips.push({
        key: 'place',
        drop: ['city', 'province'],
        icon: <MapPin className="w-3 h-3 text-champagne" aria-hidden="true" />,
        label: [n.city, n.province && provinceName(n.province, lang)].filter(Boolean).join(', '),
      });
    }
    if (n.bedrooms) chips.push({ key: 'beds', drop: ['bedrooms'], icon: <BedDouble className="w-3 h-3 text-gold" aria-hidden="true" />, label: `${n.bedrooms}+ ${translations.beds}` });
    if (n.bathrooms) chips.push({ key: 'baths', drop: ['bathrooms'], icon: <Bath className="w-3 h-3 text-gold" aria-hidden="true" />, label: `${n.bathrooms}+ ${translations.baths}` });
    if (n.minPrice || n.maxPrice) {
      chips.push({
        key: 'price',
        drop: ['minPrice', 'maxPrice'],
        icon: <DollarSign className="w-3 h-3 text-success" aria-hidden="true" />,
        label:
          n.minPrice && n.maxPrice
            ? `${formatMoney(n.minPrice, lang)} - ${formatMoney(n.maxPrice, lang)}`
            : n.minPrice
              ? `${translations.fromPrice} ${formatMoney(n.minPrice, lang)}`
              : `${translations.under} ${formatMoney(n.maxPrice!, lang)}`,
      });
    }
    if (n.propertyType) chips.push({ key: 'type', drop: ['propertyType'], icon: <Building2 className="w-3 h-3 text-gold" aria-hidden="true" />, label: propertyTypeLabel(n.propertyType, lang) });
    for (const f of featureChips(n, lang === 'fr')) chips.push({ key: f.key, drop: f.drop as (keyof NLPParsed)[], icon: <Sparkles className="w-3 h-3 text-gold" aria-hidden="true" />, label: f.label });
    return chips;
  };
  const hasNLP = (n: NLPParsed) => Boolean(n.city || hasNonPlaceParts(n));
  // The places to list: found in the browser, counted by the server.
  const placeLookup =
    hasNonPlaceParts(parsedNLP) && parsedNLP.city
      ? parsedNLP.city
      : searchQuery;
  const dealOnly = isDealWordOnly(searchQuery);
  const placeList = useMemo(
    () =>
      searchQuery.trim().length < 2 || dealOnly
        ? []
        : mergePlaceSuggestions(placeIndex ? searchPlaces(placeIndex, placeLookup, 6) : [], countedKeys, suggestions.counts, suggestions.cities, lang, 6),
    [searchQuery, dealOnly, placeIndex, placeLookup, countedKeys, suggestions, lang]
  );
  // A word that is no known place and says nothing else is not "a recognized parameter": it is a guess at a city.
  const guessOnly = (n: NLPParsed) => Boolean(n.city) && !hasNonPlaceParts(n) && placeList.length === 0;
  // Each request is numbered; an answer that comes back after a newer one was
  // sent is dropped, so a slow reply for "Mon" cannot replace the list for
  // "Montréal".
  const suggestRequest = useRef(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isPending, startTransition] = useTransition();

  const getFlatSuggestionsList = () => {
    const list: Array<{ type: 'city' | 'realtor' | 'property'; data: any }> = [];
    placeList.forEach(item => list.push({ type: 'city', data: item }));
    suggestions.realtors.forEach(item => list.push({ type: 'realtor', data: item }));
    suggestions.properties.forEach(item => list.push({ type: 'property', data: item }));
    return list;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;
    const flatList = getFlatSuggestionsList();
    if (flatList.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < flatList.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : flatList.length - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < flatList.length) {
        e.preventDefault();
        const activeItem = flatList[activeIndex];
        handleSuggestionClick(activeItem.type, activeItem.data);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Reset active keyboard index when search query or suggestions list change
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchQuery, suggestions]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('search-pending', { detail: isPending }));
  }, [isPending]);

  const containerRef = useRef<HTMLDivElement>(null);

  // Parse query on typing
  useEffect(() => {
    if (!searchQuery) {
      setParsedNLP({});
      setSuggestions({ cities: [], counts: [], realtors: [], properties: [] });
      return;
    }

    // Every rule lives in lib/search-query.ts, where a test can reach it. The
    // patterns here were written for Russian as well as English and French, on
    // a site that is English and French, and the French ones were wrong:
    // "moins de 500k" was read as a minimum.
    const parsed: NLPParsed = parseSearchQuery(searchQuery, { deal: mode });

    setParsedNLP(parsed);

    // 2. Fetch live suggestions with debouncing
    const timer = setTimeout(async () => {
      const request = ++suggestRequest.current;
      try {
        // A phrase with rooms, a price or a type in it is looked up by the
        // place it names: "Condo in Montreal 2 beds" suggests Montreal, and
        // Enter then filters by that city instead of searching the text.
        const other = hasNonPlaceParts(parsed);
        const lookup = other && parsed.city ? parsed.city : searchQuery;
        // The places the browser found are counted by the server in the same trip.
        const places = placeIndexRef.current
          ? searchPlaces(placeIndexRef.current, lookup, 6).filter((p) => p.kind === 'city').map((p) => ({ city: p.name, province: p.province }))
          : [];
        const data = await getSearchSuggestions(lookup, agencyId, places, parsed.deal ?? mode);
        if (request !== suggestRequest.current) return;
        setSuggestions(data);
        setCountedKeys(new Set(places.map((p) => placeKey(p.city, p.province))));
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error getting suggestions:', err);
      }
    }, 200);

    return () => clearTimeout(timer);
    // agencyId scopes the suggestions. Leaving it out meant that on an agency
    // showcase the list stayed scoped to whichever agency was mounted first.
  }, [searchQuery, agencyId, mode]);

  // A click outside closes the suggestions.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape closes the suggestions from wherever the focus is, not only from the field: after Tab moves on they stay open
  // and the key did nothing (the deep accessibility run of 2026-10-15).
  useEffect(() => {
    if (!showSuggestions) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSuggestions(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showSuggestions]);


  // The keyboard leaves the search for the page: the suggestions close. They hang over the first row of cards, and a focus that
  // lands on a card under them (a heart, a link) would be hidden behind them (WCAG 2.4.11, the Tab walk of 2026-10-16).
  useEffect(() => {
    if (!showSuggestions) return;
    const onFocusIn = (e: FocusEvent) => {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [showSuggestions]);

  // The address a search goes to. What the phrase says about bedrooms, price
  // and type becomes those filters; what is left is a place only when it is a
  // city the suggestions know - otherwise it is searched as text (address,
  // title, description), so a street name finds its listing instead of an
  // empty "city" of that name.
  // Where a search of this kind lists its results; an agency site searches
  // its own page.
  const targetFor = (d: 'sale' | 'rent') => (dealSwitch ? resultsPathFor(lang, d) : (resultsPath ?? pathname));
  const dealOf = (nlp: NLPParsed) => nlp.deal ?? mode;

  const searchParamsFor = (text: string, nlp: NLPParsed): URLSearchParams => {
    // A search of the other kind starts afresh: this page's price means
    // nothing there.
    const params = new URLSearchParams(dealOf(nlp) === pageDeal ? searchParams?.toString() || '' : '');
    params.delete('deal');
    for (const key of ['page', 'q', 'city', 'province', 'bedrooms', 'bathrooms', 'minPrice', 'maxPrice', 'propertyType', 'tag']) params.delete(key);
    const plain = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
    // A place the national list names exactly is a city filter at once, with
    // no wait for the server; then a city the suggestions returned.
    const listed = nlp.city && placeIndexRef.current ? resolvePlace(placeIndexRef.current, nlp.city, nlp.province) : undefined;
    // A city inside a longer phrase ("cozy Laval"): the city, and the rest as text.
    const inside = !listed && nlp.city && placeIndexRef.current ? resolvePlaceIn(placeIndexRef.current, nlp.city, nlp.province) : undefined;
    const known = listed
      ? { city: listed.name, province: listed.province }
      : inside
        ? { city: inside.place.name, province: inside.place.province }
      : nlp.city
        ? suggestions.cities.find((c) => plain(c.city) === plain(nlp.city!) && (!nlp.province || c.province === nlp.province))
        : undefined;
    if (known) {
      params.set('city', known.city);
      params.set('province', known.province);
      if (inside?.rest) params.set('q', inside.rest);
    } else {
      if (nlp.city) params.set('q', nlp.city);
      if (nlp.province) params.set('province', nlp.province);
    }
    if (nlp.bedrooms) params.set('bedrooms', String(nlp.bedrooms));
    if (nlp.bathrooms) params.set('bathrooms', String(nlp.bathrooms));
    if (nlp.minPrice) params.set('minPrice', String(nlp.minPrice));
    if (nlp.maxPrice) params.set('maxPrice', String(nlp.maxPrice));
    if (nlp.propertyType) params.set('propertyType', nlp.propertyType);
    for (const [key, value] of Object.entries(featureParams(nlp))) params.set(key, value);
    const understood = nlp.city || hasNonPlaceParts(nlp);
    if (!understood && text.trim()) params.set('q', text.trim());
    return params;
  };

  const go = (params: URLSearchParams, d: 'sale' | 'rent' = mode) => {
    setShowSuggestions(false);
    startTransition(() => {
      router.push(`${targetFor(d)}?${params.toString()}`, { scroll: false });
    });
  };

  // The switch: on the home page it sets what the next search looks for; on a
  // results page it moves to the other kind's page, keeping the place and the
  // home asked for.
  const switchDeal = (d: 'sale' | 'rent') => {
    if (d === mode) return;
    setMode(d);
    if (!deal) {
      // The home page (owner, 2026-09-25): "Rent" did nothing visible - only
      // the placeholder changed while the rows below stayed sales. It opens
      // the rentals now, searching what is already typed, if anything.
      if (!dealSwitch) return;
      if (searchQuery.trim()) {
        const nlp = withoutDropped(parseSearchQuery(searchQuery, { deal: d }));
        go(searchParamsFor(searchQuery, { ...nlp, deal: d }), d);
      } else {
        startTransition(() => router.push(resultsPathFor(lang, d), { scroll: false }));
      }
      return;
    }
    const kept = new URLSearchParams();
    for (const key of KEPT_ACROSS_DEALS) {
      const v = searchParams?.get(key);
      if (v) kept.set(key, v);
    }
    startTransition(() => {
      router.push(`${resultsPathFor(lang, d)}${kept.toString() ? `?${kept.toString()}` : ''}`, { scroll: false });
    });
  };

  const applySmartNLP = (nlp: NLPParsed) => {
    if (searchQuery.trim()) saveSearchToHistory(searchQuery);
    const params = searchParamsFor(searchQuery, nlp);
    setSearchQuery('');
    setParsedNLP({});
    go(params, dealOf(nlp));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) saveSearchToHistory(searchQuery);
    // Read the phrase now: the parse kept in state trails the last keystroke,
    // and an Enter pressed right after typing lost the last letter.
    const nlp = withoutDropped(parseSearchQuery(searchQuery, { deal: mode }));
    go(searchParamsFor(searchQuery, nlp), dealOf(nlp));
  };

  const clearSearch = () => {
    setSearchQuery('');
    setParsedNLP({});
    
    const params = new URLSearchParams(searchParams?.toString() || '');
    params.delete('page');
    params.delete('q');
    params.delete('city');
    params.delete('province');
    params.delete('bedrooms');
    params.delete('bathrooms');
    params.delete('maxPrice');
    params.delete('propertyType');
    params.delete('tag');
    params.delete('minPrice');

    startTransition(() => {
      router.push(`${targetFor(mode)}?${params.toString()}`, { scroll: false });
    });
  };

  const handleSuggestionClick = (type: 'city' | 'realtor' | 'property', item: any) => {
    const params = new URLSearchParams(mode === pageDeal ? searchParams?.toString() || '' : '');
    params.delete('deal');
    params.delete('page');
    if (type === 'city') {
      const place = item as PlaceSuggestion;
      saveSearchToHistory(place.label);
      if (place.city) params.set('city', place.city);
      else params.delete('city');
      params.set('province', place.province);
      params.delete('q');
      setSearchQuery('');
    } else if (type === 'realtor') {
      router.push(agencyPath(lang, item));
      return;
    } else if (type === 'property') {
      // Suggestions are of the kind being searched: a rental's address when renting.
      router.push(listingPathFor(lang, { ...item, listing_type: item.listing_type ?? mode }));
      return;
    }

    startTransition(() => {
      router.push(`${targetFor(mode)}?${params.toString()}`, { scroll: false });
    });
    setShowSuggestions(false);
  };

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center justify-center relative" id="home-search-header-container">
      {dealSwitch && (
        // Buy or rent (rentals, 2026-09-24): as on Rightmove, Centris and
        // Realtor.ca, a switch in the search, not a filter among others.
        <div role="group" aria-label={isFr ? 'Je cherche à' : 'I want to'} className="mb-2.5 inline-flex rounded-full bg-neutral-900 border border-neutral-800 p-1">
          {(['sale', 'rent'] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={mode === d}
              onClick={() => switchDeal(d)}
              className={`h-8 min-w-20 px-4 rounded-full text-sm font-semibold transition-colors cursor-pointer ${mode === d ? 'bg-champagne text-black' : 'text-neutral-300 hover:text-white'}`}
            >
              {d === 'sale' ? (isFr ? 'Acheter' : 'Buy') : isFr ? 'Louer' : 'Rent'}
            </button>
          ))}
        </div>
      )}
      {/* Main Search Bar */}
      <form 
        onSubmit={handleSearch}
        className="w-full max-w-5xl flex flex-row items-center bg-neutral-900 border border-neutral-800 rounded-full p-1.5 md:p-2 shadow-2xl shadow-black/20 has-[.search-field:focus]:border-champagne has-[.search-field:focus]:ring-2 has-[.search-field:focus]:ring-champagne transition-all gap-1.5 relative z-20"
      >
        {/* On a phone 10px from the bar's edge to the icon (6px bar padding + 4px
            here), matching the other end and the page gutter. */}
        <div className="flex-1 flex items-center gap-2 md:gap-3 pl-3 pr-2 sm:px-3 md:px-4 py-1.5 md:py-2 min-w-0">
          <div className="relative flex-1 min-w-0">
          <input
            type="text"
            role="combobox"
            // A placeholder is not a name: without this the field was announced
            // only as "combobox".
            aria-label={isFr ? 'Rechercher une propriété' : 'Search for a property'}
            aria-expanded={showSuggestions}
            // A combobox has to name the thing it expands, or assistive
            // software announces that something opened without being able to
            // reach it. aria-expanded alone was not enough.
            aria-controls="home-search-suggestions"
            // The panel holds buttons, chips and lists, not only options, so it is a
            // dialog the combobox opens; a listbox with no options in it is invalid.
            aria-haspopup="dialog"
            aria-autocomplete="list"
            maxLength={MAX_PHRASE}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setDropped([]);
              ensurePlaces();
            }}
            onFocus={() => {
              ensurePlaces();
              setShowSuggestions(true);
              setActiveIndex(-1);
              setSearchFocused(true);
            }}
            onBlur={() => setSearchFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder={isSmartSearchEnabled ? translations.placeholder : translations.placeholderStandard}
            // The field grows into the padding its wrapper already reserves, so
            // a tap just above or below the text lands on the input rather than
            // on dead space. The negative margin keeps the bar its old height.
            //
            // 16px on a phone: a smaller field makes iOS zoom the page on focus.
            // The native placeholder is hidden there in favour of the hints.
            // search-field: no focus outline of its own (site.css) - the whole
            // bar takes the one thin line instead (has-[.search-field:focus]).
            className="search-field w-full bg-transparent border-none outline-none py-1.5 -my-1.5 text-white placeholder:text-transparent sm:placeholder:text-neutral-400 text-base sm:text-sm md:text-base font-medium truncate"
          />
          {!searchQuery && (
            <span aria-hidden="true" className="sm:hidden pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center overflow-hidden text-base font-medium text-neutral-400">
              <span
                className={`truncate transition-opacity duration-500 ease-in-out motion-reduce:transition-none ${hintVisible || searchFocused ? 'opacity-100' : 'opacity-0'}`}
              >
                {searchHints[hintIndex]}
              </span>
            </span>
          )}
          </div>
          {searchQuery && (
            <button aria-label={isFr ? 'Effacer la recherche' : 'Clear search'} 
              type="button" 
              onClick={clearSearch}
              className="text-neutral-400 hover:text-white transition-colors shrink-0 p-1.5 -m-1.5"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 md:gap-2 px-1 shrink-0">
          {filtersButton && (
            <button
              type="button"
              onClick={() => {
                setFiltersStart(searchParamsFor(searchQuery, parseSearchQuery(searchQuery, { deal: mode })).toString());
                setFiltersOpen(true);
              }}
              onMouseEnter={preloadFilterPanel}
              onFocus={preloadFilterPanel}
              onTouchStart={preloadFilterPanel}
              aria-haspopup="dialog"
              aria-label={isFr ? 'Filtres' : 'Filters'}
              // The word from 640px up; a phone shows the icon alone (owner's choice).
              className="flex items-center justify-center gap-1.5 h-11 w-11 sm:w-auto sm:px-3.5 rounded-full bg-neutral-800 hover:bg-neutral-700 text-sm font-semibold text-white transition-colors shrink-0 cursor-pointer"
            >
              <SlidersHorizontal aria-hidden="true" className="w-4 h-4" />
              <span className="hidden sm:inline">{isFr ? 'Filtres' : 'Filters'}</span>
            </button>
          )}
          <button
            type="submit"
            className="flex items-center justify-center w-11 h-11 bg-champagne hover:bg-champagne/85 text-black rounded-full transition shadow-md shadow-champagne/10 shrink-0 hover:scale-105 active:scale-95"
            aria-label={isFr ? 'Rechercher' : 'Search'}
            title={isFr ? 'Rechercher' : 'Search'}
          >
            <Search className="w-4 h-4 md:w-5 md:h-5 stroke-[2.5]" />
          </button>
        </div>
      </form>
      {filtersButton && (
        <LazyFilterPanel
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          resultsPath={dealSwitch ? targetFor(mode) : resultsPath}
          deal={mode}
          // What is typed so far counts too: "Montreal" plus the panel's price.
          startQuery={filtersOpen ? filtersStart : ''}
          agencyId={agencyId ?? null}
        />
      )}

      {/* Typeahead Suggestions Dropdown with Guided Examples & Structured Smart Filter Match */}
          <Transition
            show={showSuggestions}
            from={{ opacity: 0, y: 10 }}
            duration={150}
            id="home-search-suggestions"
            role="dialog"
            aria-modal="false"
            aria-label={isFr ? 'Suggestions de recherche' : 'Search suggestions'}
            className="absolute top-[calc(100%+8px)] left-0 right-0 w-full max-w-5xl bg-neutral-900 border border-neutral-800 rounded-3xl mt-1 p-4 sm:p-5 shadow-2xl z-50 overflow-hidden"
          >
            
            {/* Empty search input or short query: Show guided autocomplete suggestion chips and natural language instructions */}
            {searchQuery.trim().length < 2 ? (
              <div className="space-y-4">
                {/* Search History (Point 2) */}
                {recentSearches.length > 0 && (
                  <div className="pb-3 border-b border-neutral-800/60">
                    <div className="flex justify-between items-center mb-2 px-1">
                      <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-widest flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-champagne" />
                        {translations.recentSearches}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setRecentSearches([]);
                          localStorage.removeItem('recent_searches');
                        }}
                        className="text-xs font-bold text-danger hover:text-danger transition-colors cursor-pointer"
                      >
                        {translations.clear}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((term, idx) => (
                        <button
                          key={`recent-${idx}`}
                          type="button"
                          onClick={() => {
                            setSearchQuery(term);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-full text-xs font-semibold transition cursor-pointer active:scale-95 border border-neutral-800"
                        >
                          <Clock className="w-3 h-3 text-neutral-400" />
                          <span>{term}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3 bg-neutral-950 p-4 rounded-2xl border border-neutral-900">
                  <HelpCircle className="w-5 h-5 text-champagne shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-sm font-bold text-neutral-100">
                      {isSmartSearchEnabled ? translations.howToSearch : translations.howToSearchStandard}
                    </h2>
                    <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
                      {isSmartSearchEnabled ? translations.howToSearchDesc : translations.howToSearchDescStandard}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-widest block mb-2 px-1">
                    {isSmartSearchEnabled ? translations.trySearching : translations.trySearchingStandard}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {isSmartSearchEnabled ? (
                      [
                        isFr ? "Toronto max 1.5M" : "Toronto under 1.5M",
                        isFr ? "Condo à Montréal 2 chambres" : "Condo in Montreal 2 beds",
                        isFr ? "Maison à Vancouver max 800k" : "House in Vancouver under 800k",
                        isFr ? "Vancouver 3 chambres" : "Vancouver 3 beds"
                      ].map((tipText, idx) => (
                        <button
                          key={`tip-${idx}`}
                          type="button"
                          onClick={() => {
                            setSearchQuery(tipText);
                          }}
                          className="px-3.5 py-2 bg-neutral-800 hover:bg-champagne/10 hover:text-champagne text-neutral-300 rounded-full text-xs font-semibold transition cursor-pointer active:scale-95"
                        >
                          {tipText}
                        </button>
                      ))
                    ) : (
                      [
                        isFr ? "Toronto" : "Toronto",
                        isFr ? "Montréal" : "Montreal",
                        isFr ? "Vancouver" : "Vancouver",
                        isFr ? "Ottawa" : "Ottawa",
                        isFr ? "Calgary" : "Calgary"
                      ].map((cityText, idx) => (
                        <button
                          key={`city-chip-${idx}`}
                          type="button"
                          onClick={() => {
                            setSearchQuery(cityText);
                          }}
                          className="px-3.5 py-2 bg-neutral-800 hover:bg-champagne/10 hover:text-champagne text-neutral-300 rounded-full text-xs font-semibold transition cursor-pointer active:scale-95"
                        >
                          {cityText}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Active typing: show real-time NLP parsed parameters AND DB suggestion matching list
              <div className="space-y-4">
                
                {/* NLP Parsed breakdown card */}
                {isSmartSearchEnabled && hasNLP(effectiveNLP) && !guessOnly(effectiveNLP) && (
                  <Transition
                    show
                    appear
                    collapse
                    from={{ opacity: 0 }}
                    duration={200}
                    className="pb-3 border-b border-neutral-800/60"
                  >
                    <div className="bg-champagne/5 border border-champagne/20 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-champagne animate-pulse shrink-0" />
                          <span className="text-xs font-bold text-neutral-200">
                            {translations.smartMatch}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {nlpChips(effectiveNLP).map((chip) => (
                            <span key={chip.key} className="flex items-center gap-1 bg-neutral-900 pl-2.5 pr-1 py-1 rounded-lg text-xs font-semibold text-neutral-300 border border-neutral-800 shadow-sm">
                              {chip.icon}
                              {chip.label}
                              <button
                                type="button"
                                onClick={() => setDropped((d) => [...d, ...chip.drop])}
                                aria-label={`${translations.removeChip} ${chip.label}`}
                                className="ml-0.5 w-6 h-6 rounded-md flex items-center justify-center text-neutral-400 hover:text-white hover:bg-neutral-800 cursor-pointer"
                              >
                                <X className="w-3 h-3" aria-hidden="true" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => applySmartNLP(effectiveNLP)}
                        className="px-4 py-2 bg-champagne hover:bg-champagne/85 text-black font-bold text-xs rounded-xl shadow-md transition self-start md:self-center shrink-0"
                      >
                        {translations.applyFilters}
                      </button>
                    </div>
                  </Transition>
                )}

                {/* Places: Canada's municipalities and provinces, found in the
                    browser, with the listings the server counted (lib/place-search.ts). */}
                {placeList.length > 0 && (
                  <div className="pb-1">
                    <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-widest block mb-2 px-1">
                      {translations.cities}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {placeList.map((item, idx) => {
                        const isActive = activeIndex === idx;
                        return (
                          <button
                            key={`place-${item.kind}-${item.label}`}
                            type="button"
                            onClick={() => handleSuggestionClick('city', item)}
                            className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-white text-sm font-semibold transition duration-150 border border-transparent ${
                              isActive
                                ? 'bg-neutral-800 border-champagne/30 shadow-sm ring-1 ring-champagne/50 scale-[1.01]'
                                : 'hover:bg-neutral-950'
                            }`}
                          >
                            <MapPin className="w-4 h-4 text-champagne shrink-0" aria-hidden="true" />
                            <span className="min-w-0 flex-1 truncate">
                              <HighlightMatch text={item.label} highlight={placeLookup} />
                              {item.kind === 'province' && <span className="text-neutral-400 font-normal"> · {translations.province}</span>}
                            </span>
                            {item.listings !== null && (
                              <span className={`shrink-0 text-xs font-normal ${item.listings > 0 ? 'text-neutral-400' : 'text-neutral-400'}`}>
                                {item.listings > 0 ? translations.listingsCount(item.listings) : translations.noListingsYet}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Database Results - Realtors */}
                {suggestions.realtors.length > 0 && (
                  <div className="pt-3 border-t border-neutral-800/60">
                    <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-widest block mb-2 px-1">
                      {translations.realtors}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {suggestions.realtors.map((realtor, idx) => {
                        const globalIndex = placeList.length + idx;
                        const isActive = activeIndex === globalIndex;
                        return (
                          <button
                            key={`realtor-${idx}`}
                            type="button"
                            onClick={() => handleSuggestionClick('realtor', realtor)}
                            className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-white text-sm font-semibold transition duration-150 border border-transparent ${
                              isActive 
                                ? 'bg-neutral-800 border-champagne/30 shadow-sm ring-1 ring-champagne/50 scale-[1.01]' 
                                : 'hover:bg-neutral-950'
                            }`}
                          >
                            <User className="w-4 h-4 text-gold" />
                            <span>
                              <HighlightMatch text={realtor.name} highlight={searchQuery} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Database Results - Properties */}
                {suggestions.properties.length > 0 && (
                  <div className="pt-3 border-t border-neutral-800/60">
                    <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-widest block mb-2 px-1">
                      {translations.properties}
                    </span>
                    <div className="space-y-1">
                      {suggestions.properties.map((prop, idx) => {
                        const globalIndex = placeList.length + suggestions.realtors.length + idx;
                        const isActive = activeIndex === globalIndex;
                        return (
                          <button
                            key={`prop-${idx}`}
                            type="button"
                            onClick={() => handleSuggestionClick('property', prop)}
                            className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-white text-sm font-semibold transition duration-150 border border-transparent ${
                              isActive 
                                ? 'bg-neutral-800 border-champagne/30 shadow-sm ring-1 ring-champagne/50 scale-[1.01]' 
                                : 'hover:bg-neutral-950'
                            }`}
                          >
                            <Home className="w-4 h-4 text-success" />
                            <div className="truncate">
                              <span className="block truncate">
                                <HighlightMatch text={(isFr ? prop.title_fr || prop.title_en : prop.title_en || prop.title_fr) || ''} highlight={searchQuery} />
                              </span>
                              <span className="text-xs text-neutral-400">{prop.city}, {prop.province}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Fallback when typing but no Database suggestion matches */}
                {placeList.length === 0 && suggestions.realtors.length === 0 && suggestions.properties.length === 0 && (
                  <div className="pt-3">
                    {isSmartSearchEnabled && hasNLP(parsedNLP) ? (
                      <>
                        <p className="text-xs text-neutral-400 italic px-1">
                          {guessOnly(parsedNLP) ? translations.noMatchAtAll : translations.noDbMatches}
                        </p>
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {!guessOnly(parsedNLP) && (parsedNLP.city || parsedNLP.province) && (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <MapPin className="w-3.5 h-3.5 text-gold" />
                              {[parsedNLP.city, parsedNLP.province && provinceName(parsedNLP.province, lang)].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {parsedNLP.bedrooms && (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <BedDouble className="w-3.5 h-3.5 text-gold" />
                              {parsedNLP.bedrooms}+ {translations.beds}
                            </span>
                          )}
                          {parsedNLP.bathrooms && (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <Bath className="w-3.5 h-3.5 text-gold" />
                              {parsedNLP.bathrooms}+ {translations.baths}
                            </span>
                          )}
                          {parsedNLP.minPrice && parsedNLP.maxPrice ? (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <DollarSign className="w-3.5 h-3.5 text-success" />
                              {formatMoney(parsedNLP.minPrice, lang)} - {formatMoney(parsedNLP.maxPrice, lang)}
                            </span>
                          ) : parsedNLP.minPrice ? (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <DollarSign className="w-3.5 h-3.5 text-success" />
                              {translations.fromPrice} {formatMoney(parsedNLP.minPrice, lang)}
                            </span>
                          ) : parsedNLP.maxPrice ? (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <DollarSign className="w-3.5 h-3.5 text-success" />
                              {translations.under} {formatMoney(parsedNLP.maxPrice, lang)}
                            </span>
                          ) : null}
                          {parsedNLP.propertyType && (
                            <span className="flex items-center gap-1 bg-neutral-950 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-neutral-400 border border-neutral-800">
                              <Building2 className="w-3.5 h-3.5 text-gold" />
                              {propertyTypeLabel(parsedNLP.propertyType ?? '', lang)}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-neutral-400 italic px-1">
                        {isFr ? 'Aucun résultat trouvé' : 'No results found'}
                      </p>
                    )}
                  </div>
                )}

              </div>
            )}
          </Transition>

    </div>
  );
}
